import styled from 'styled-components';
import strings from 'strings/strings.json';
import { FlexAlignCenter, FlexEnd, WrappingText } from 'components/Elements';
import { CourseSchedule } from 'models/CourseSchedule';
import { sizes, theme } from 'theme';
import Table, { TableCell } from 'components/table/Table';
import useWindowWidth from 'hooks/useWindowWidth';
import { LocationType } from '../../../../models/ActivityMeeting';
import { IconSize } from '../../../../assets/icons/icon-sizes';
import NavigateWithArrow from '../../../../components/actions/NavigateWithArrow';
import VideoChatIcon from '../../../../assets/icons/pathway/VideoChatIcon';

type ScheduledLiveTrainingProps = {
    courseSchedule: CourseSchedule;
};

export function ScheduledLiveTrainingsTable({ courseSchedule }: ScheduledLiveTrainingProps): JSX.Element {
    const { isMobileWidth } = useWindowWidth();

    return (
        <Table
            headers={[
                {
                    name: strings.scheduledCourseDetailsPage.headers.liveTraining,
                    rowPercentage: 40,
                },
                {
                    name: strings.scheduledCourseDetailsPage.headers.startDate,
                    rowPercentage: 15,
                },
                {
                    name: strings.scheduledCourseDetailsPage.headers.time,
                    rowPercentage: 15,
                },
                {
                    name: strings.scheduledCourseDetailsPage.headers.location,
                    rowPercentage: 20,
                },
                {
                    name: '',
                    rowPercentage: 10,
                    rightAligned: true,
                    mobileRightColumn: true,
                },
            ]}
            rows={
                courseSchedule.meetings.map((meeting) => {
                    const d = new Date(meeting.meetingDatetime);
                    const shortDate = d.toLocaleDateString();
                    const time = d.toLocaleString('en-GB', {
                        hour: 'numeric',
                        minute: 'numeric',
                        hourCycle: 'h12',
                    });

                    const location =
                        // eslint-disable-next-line eqeqeq
                        meeting.locationType == LocationType.ONLINE
                            ? strings.scheduledCourseDetailsPage.onlineLocation
                            : meeting.location ?? '';

                    const linkHref = `/live-course/${courseSchedule.id}/live-training/${meeting.activityId}`;

                    const cells: TableCell[] = [
                        {
                            name: strings.scheduledCourseDetailsPage.headers.liveTraining,
                            value: (
                                <FlexAlignCenter key={meeting.id}>
                                    {!isMobileWidth && (
                                        <IconContainer>
                                            <VideoChatIcon />
                                        </IconContainer>
                                    )}
                                    <WrappingText $bold>{meeting.activity.title}</WrappingText>
                                </FlexAlignCenter>
                            ),
                        },
                        {
                            name: strings.scheduledCourseDetailsPage.headers.startDate,
                            value: shortDate,
                        },
                        {
                            name: strings.scheduledCourseDetailsPage.headers.time,
                            value: time,
                        },
                        {
                            name: strings.scheduledCourseDetailsPage.headers.location,
                            value: location,
                        },
                        {
                            name: '',
                            value: (
                                <FlexEnd>
                                    <NavigateWithArrow
                                        title={strings.scheduledCourseDetailsPage.viewLiveMeetingDetails}
                                        aria={strings.liveTrainingDetailPage.detailLinkAria}
                                        href={linkHref}
                                        iconSize={IconSize.LARGE}
                                    />
                                </FlexEnd>
                            ),
                        },
                    ];

                    return {
                        id: meeting.id,
                        cells: cells,
                    };
                }) ?? []
            }
        />
    );
}

const IconContainer = styled(FlexAlignCenter)`
    background-color: ${theme.iconBackgroundColour};
    border-radius: ${sizes.borderRadiusMd};
    margin-right: ${sizes.spacingSm};
    padding: 3px;
`;
